/* eslint-disable no-underscore-dangle */
import React from 'react';
import ReactDOM from 'react-dom';
import Dialog from '@react/react-spectrum/Dialog';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import { interpretKey } from '../../utils/eventUtil';

class DialogIntenals {
  mainQueue = [];

  persistenceQueue = [];

  modalKey = 1;

  currentDialogKey = null;

  dialogEventInfo = null;

  constructor() {
      window.addEventListener('keydown', interpretKey({
          onEscape: [(e) => {
              // [CCD-16986] On Escape Event and a dialog is open with some dialogs
              // in persistence queue, store the event info.
              if (this.currentDialogKey && this.persistenceQueueSize > 0) {
                  this.dialogEventInfo = {
                      event: e,
                      dialogID: this.currentDialogKey,
                  };
              }
          }, false, false],
      }), true);
  }

  get mainQueueSize() {
      return this.mainQueue.length;
  }

  get persistenceQueueSize() {
      return this.persistenceQueue.length;
  }

  // Execute onClose callback if this dialog is deleted from both the queues
  onClose = (key, dialogData) => {
      const { onClose } = dialogData.props[1];
      if (!onClose) {
          return;
      }
      const { mainQueue, persistenceQueue } = this;
      const comparator = t => t.localKey === key;
      if (!persistenceQueue.find(comparator) && !mainQueue.find(comparator)) {
          onClose(key);
      }
  }

  handleDialogRef = (dialogRef, isVisible, lastActiveElement = null) => {
      if (!(dialogRef && dialogRef.current)) {
          return;
      }
      // eslint-disable-next-line react/no-find-dom-node
      const domNode = ReactDOM.findDOMNode(dialogRef.current);
      if (domNode) {
          const { parentElement } = domNode;
          if (parentElement) {
              if (isVisible) {
                  parentElement.style.display = 'unset';
                  if (lastActiveElement) {
                      lastActiveElement.focus();
                  }
              } else {
                  parentElement.style.display = 'none';
              }
          }
      }
  }

  showDialog = (dialogData) => {
      const {
          localKey, persist, ref, props,
      } = dialogData;
      const [content, config, dialogView] = props;
      const onClose = (...params) => {
          this.hideDialog(localKey);
          // Modal Container calls onClose for the persisted dialog on click of esc.
          // Not allowing esc event to propagate
          if (this.dialogEventInfo) {
              const { event, dialogID } = this.dialogEventInfo;
              if (event && dialogID === localKey) {
                  event.stopImmediatePropagation();
                  this.dialogEventInfo = null;
              }
          }
      };
      let dialogKey = null;
      let dialogRef = null;
      let dialogContent = null;
      if (persist && ref) {
          const { key, lastActiveElement } = dialogData;
          dialogRef = ref;
          dialogKey = key;
          this.handleDialogRef(ref, true, lastActiveElement);
      } else {
          dialogRef = React.createRef();
          if (config.wrapInDialog !== undefined && config.wrapInDialog === false) {
              dialogContent = React.cloneElement(content, { ...config, onClose, ref: dialogRef });
          } else {
              dialogContent = (
                  <Dialog
                      {...config}
                      ref={dialogRef}
                      onClose={onClose}>
                      {content}
                  </Dialog>
              );
          }
          dialogKey = ModalContainer.show(
              dialogContent,
              dialogView
          );
      }
      if (config.onDialogShow) {
          config.onDialogShow(dialogKey);
      }
      this.currentDialogKey = localKey;
      return {
          key: dialogKey,
          ref: dialogRef,
      };
  }

  hideDialog = (key, pollQueueRequired = true, explicit = false, persist = false) => {
      const { mainQueue, pollQueue, persistenceQueue } = this;
      const comparator = t => t.localKey === key;
      const mainQueuekeyIndex = mainQueue.findIndex(comparator);
      const persistenceQueuekeyIndex = persistenceQueue.findIndex(comparator);

      const dialogData = mainQueue[mainQueuekeyIndex] || persistenceQueue[persistenceQueuekeyIndex];

      if (this.currentDialogKey === key) {
          this.currentDialogKey = null;
      }

      // Deleting from main queue
      if (mainQueuekeyIndex !== -1) {
          const { key: dialogKey, ref: dialogRef, props } = mainQueue[mainQueuekeyIndex];
          if (dialogRef && persist) {
              dialogData.persist = true;
              dialogData.lastActiveElement = document.activeElement;
              this.handleDialogRef(dialogRef, false);
          } else if (dialogKey) {
              if (props[1].onDialogHide) {
                  props[1].onDialogHide(dialogKey);
              }
              ModalContainer.hide(dialogKey);
          }
          mainQueue.splice(mainQueuekeyIndex, 1);
          if (pollQueueRequired) pollQueue();
      }

      // Deleting from persistense queue
      if (explicit && persistenceQueuekeyIndex !== -1) {
          persistenceQueue.splice(persistenceQueuekeyIndex, 1);
      }

      if (!explicit && dialogData) {
          this.onClose(key, dialogData);
      }
  }

  increaseKey = () => {
      const temp = this.modalKey;
      this.modalKey = this.modalKey + 1;
      return temp;
  }

  /**
   * Checks if there are any dialogs in persistenceQueue or mainQueue.
   * @param  {Boolean} [onlyMainQueue=false] only check the mainQueue
   */
  pollQueue = (onlyMainQueue = false) => {
      const {
          mainQueue,
          showDialog,
          mainQueueSize,
          persistenceQueueSize,
          persistenceQueue,
      } = this;
      if (mainQueueSize === 0 && persistenceQueueSize === 0) {
          return;
      }
      if (!onlyMainQueue && persistenceQueueSize) {
          const lastDialog = persistenceQueue.pop();
          if (!lastDialog.persist) {
              lastDialog.key = undefined;
          }
          // Move last persisted dialog to main queue and poll main queue
          mainQueue.push(lastDialog);
          this.pollQueue(true);
          return;
      }
      const top = mainQueue[mainQueueSize - 1];
      if (top && mainQueue.filter(d => !d.persist && d.key !== undefined).length === 0) {
          const ret = showDialog(top);
          top.key = ret.key;
          top.ref = ret.ref;
          top.persist = false;
      }
  };

  /**
   * Hide all non-mandatory dialogs
   */
  removeNonMandatoryDialogs = () => {
      const cloneQueue = [...this.mainQueue];
      cloneQueue.forEach((dialog) => {
          if (!dialog.props[1].mandatory) {
              this.hideDialog(dialog.localKey, false);
          }
      });
  };

  /**
   * Moves current dialog to persistence queue
   * This should be called when an immediate dialog comes in
   */
  persistCurrentDialog = () => {
      const { currentDialogKey } = this;
      if (!currentDialogKey) {
          return;
      }
      const { mainQueue, pollQueue } = this;
      const keyIndex = mainQueue.findIndex(t => t.localKey === currentDialogKey);
      if (keyIndex === -1) {
          return;
      }
      this.persistenceQueue.push(mainQueue[keyIndex]);
      this.hideDialog(currentDialogKey, false, false, true);
  };
}

export default class DialogManager {
    constructor() {
        this._dialogInternals = new DialogIntenals();
    }

    /**
     * Show a dialog
     * @param  {SpectrumDialog} content to be shown
     * @param  {Object} configure priority of dialog with following options
     *         immediate: Dialog will be shown immediately. If there is a mandatory dialog currently
     *                    being shown, it will be hidden for now and shown again after immediate
     *                    dialog is closed.
     *         mandatory: Mandatory dialogs are not hidden automatically and only hide on some
     *                    user action.
     *         onDialogShow: The action to be called when this dialog is shown
     *                      (will be called even when dialog is pulled from background)
     *         onDialogHide: The action to be called when this dialog is hidden.
     *                      (will be called even when dialog goes to background)
     *         wrapInDialog: If the content should be wrapped in Dialog component
     * @param  {Object}  Reference of react component where this dialog will be attached
     * @return {integer} This key should be used to hide dialog
     */
    show(content, config = { mandatory: false, immediate: false, wrapInDialog: true }, dialogView) {
        const {
            increaseKey,
            mainQueue,
            pollQueue,
            removeNonMandatoryDialogs,
            persistCurrentDialog,
        } = this._dialogInternals;

        const localKey = increaseKey();
        const dialogData = { props: [content, config, dialogView], localKey };
        if (config.immediate) {
            removeNonMandatoryDialogs();
            persistCurrentDialog();
            mainQueue.push(dialogData);
        } else {
            mainQueue.unshift(dialogData);
        }
        pollQueue(true);
        return localKey;
    }

    /**
     * Hide a dialog
     * @param  {integer} pass key for the dialog, which was returned by show method.
     */
    hide(key) {
        this._dialogInternals.hideDialog(key, true, true);
    }

    /**
     * Dismiss all non-mandatory dialogs
     */
    dismissAllNonMandatoryDialogs() {
        this._dialogInternals.removeNonMandatoryDialogs();
    }
}
